import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  signal
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

import { itemsColors } from '@core/models/colors.model';
import { isHexColor, isNil, normalizeHexColor } from '@core/utils/shared-utils';

import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';

const DEFAULT_COLORS = Object.values(itemsColors);
const FALLBACK_COLOR = '#6366f1';

@Component({
  selector: 'app-color-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, DropdownComponent, TranslatePipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPickerComponent),
      multi: true
    }
  ],
  templateUrl: './color-picker.component.html'
})
export class ColorPickerComponent implements ControlValueAccessor {
  /** Preset color swatches shown in the panel. Defaults to the app's item color palette. */
  colors = input<string[]>(DEFAULT_COLORS);
  /**
   * Lets the value be `null`: an unset value is shown as "no color" instead of the
   * fallback swatch, and the panel offers to clear the color.
   */
  clearable = input(false);

  protected readonly currentValue = signal(FALLBACK_COLOR);
  protected readonly hexInputValue = signal(FALLBACK_COLOR);
  protected readonly isUnset = signal(false);
  protected readonly isOpen = signal(false);
  protected readonly isDisabled = signal(false);

  protected readonly showsNoColor = computed(() => this.clearable() && this.isUnset());
  /**
   * `<input type="color">` only accepts a lowercase `#rrggbb` and silently shows black for
   * anything else, so the `#RGB` shorthand some entity schemas allow is expanded first.
   */
  protected readonly nativeColor = computed(
    () => normalizeHexColor(this.currentValue()) ?? FALLBACK_COLOR
  );

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  // ControlValueAccessor

  writeValue(value: string | null): void {
    const v = value || FALLBACK_COLOR;
    this.isUnset.set(isNil(value) || value === '');
    this.currentValue.set(v);
    this.hexInputValue.set(v);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  // Panel open/close

  protected toggle(): void {
    if (this.isDisabled()) return;
    this.isOpen.update((v) => !v);
  }

  protected close = (): void => {
    this.isOpen.set(false);
    this.onTouched();
  };

  // Color selection

  protected selectPreset(color: string): void {
    this.isUnset.set(false);
    this.currentValue.set(color);
    this.hexInputValue.set(color);
    this.onChange(color);
    this.close();
  }

  protected onColorPickerInput(event: Event): void {
    const hex = (event.target as HTMLInputElement).value;
    this.isUnset.set(false);
    this.currentValue.set(hex);
    this.hexInputValue.set(hex);
    this.onChange(hex);
  }

  protected onHexInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.hexInputValue.set(value);
    if (isHexColor(value)) {
      this.isUnset.set(false);
      this.currentValue.set(value);
      this.onChange(value);
    }
  }

  // Swatch state

  protected isPresetSelected(color: string): boolean {
    return !this.showsNoColor() && this.currentValue() === color;
  }

  protected swatchStateClass(selected: boolean): string {
    return selected ? 'ring-2 ring-offset-2 ring-zinc-800 scale-110' : 'hover:scale-110';
  }

  protected clear(): void {
    this.isUnset.set(true);
    this.onChange(null);
    this.close();
  }
}
